import {
  ErrorComponent,
  type ErrorComponentProps,
  Link,
  rootRouteId,
  useMatch,
  useRouter,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { captureException } from "@/lib/sentry";

function isChunkLoadError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("Unable to preload CSS")
  );
}

export function DefaultCatchBoundary({ error }: ErrorComponentProps) {
  const router = useRouter();
  const isRoot = useMatch({
    strict: false,
    select: (state) => state.id === rootRouteId,
  });

  console.error("DefaultCatchBoundary Error:", error);

  // Auto-reload on chunk load failures (stale deployment — old chunk hashes no longer exist)
  useEffect(() => {
    if (isChunkLoadError(error)) {
      window.location.reload();
    }
  }, [error]);

  if (isChunkLoadError(error)) {
    return (
      <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-4 p-4">
        <p className="text-sm text-muted-foreground">Updating to the latest version…</p>
      </div>
    );
  }

  captureException(error);

  return (
    <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-6 p-4">
      <ErrorComponent error={error} />
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => {
            router.invalidate();
          }}
          className="rounded bg-muted px-2 py-1 text-sm text-foreground uppercase"
        >
          Try Again
        </button>
        {isRoot ? (
          <Link to="/" className="rounded bg-muted px-2 py-1 text-sm text-foreground uppercase">
            Home
          </Link>
        ) : (
          <Link
            to="/"
            className="rounded bg-muted px-2 py-1 text-sm text-foreground uppercase"
            onClick={(e) => {
              e.preventDefault();
              window.history.back();
            }}
          >
            Go Back
          </Link>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        If this problem persists, please contact{" "}
        <a href="mailto:support@setupsbyszese.com" className="text-primary underline">
          support@setupsbyszese.com
        </a>
      </p>
    </div>
  );
}
